import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getContractItem } from "../../API/billingAPI";
import {
  Box,
  Button,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Stack,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  CardHeader,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
// import DeleteContract from "../DeleteContract";

const getContractStatusStyles = (status) => {
  switch (status) {
    case 'ACTIVE':
      return { backgroundColor: '#d4edda', color: '#155724' };
    case 'EXPIRED':
      return { backgroundColor: '#f8d7da', color: '#721c24' };
    case 'TERMINATED':
      return { backgroundColor: '#e0e0e0', color: '#424242' };
    case 'INACTIVE':
    default:
      return { backgroundColor: '#fff9c4', color: '#978d2d' };
  }
};

function ContractsTable({
  source,
  selectedClient,
  contracts,
  loading: externalLoading,
  onDeleteSuccess,
  hasSearched
}) {
  const CURRENCY = 'PHP';
  const navigate = useNavigate();
  const [ menuAnchor, setMenuAnchor ] = useState(null);
  const [ selectedContractForMenu, setSelectedContractForMenu ] = useState(null);
  const [ sortConfig, setSortConfig ] = useState({
    order: 'desc',
  });
  const [ statusFilter, setStatusFilter ] = useState('');
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  // const [ deleteDialogOpen, setDeleteDialogOpen ] = useState(false);

  const isExternalMode = contracts !== undefined;
  const [ internalContracts, setInternalContracts ] = useState([]);
  const [ internalLoading, setInternalLoading ] = useState(false);
  const displayedContracts = isExternalMode ? contracts : internalContracts;
  const loading = isExternalMode ? externalLoading : internalLoading;
  const sortedContracts = useMemo(() => {
    const filteredContracts = statusFilter
      ? displayedContracts.filter((contract) => contract.status === statusFilter)
      : displayedContracts;

    return [...filteredContracts].sort((a, b) => {
      let comparison = 0;

      const dateA = a.contract_start ? new Date(a.contract_start).getTime() : null;
      const dateB = b.contract_start ? new Date(b.contract_start).getTime() : null;

      if (dateA === null && dateB === null) comparison = 0;
      else if (dateA === null) return 1;
      else if (dateB === null) return -1;
      else comparison = dateA - dateB;

      return sortConfig.order === 'asc' ? comparison : -comparison;
    });
  }, [displayedContracts, sortConfig.order, statusFilter]);
  const totalRows = sortedContracts.length;
  const paginatedContracts = sortedContracts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  useEffect(() => {
    if (isExternalMode || !selectedClient?.id || !hasSearched) return;
    let active = true;
    setInternalLoading(true);
    getContractItem(selectedClient.id)
      .then((data) => {
        if (!active) return;
        setInternalContracts(data?.contracts ?? []);
      })
      .catch(() => { if (active) setInternalContracts([]); })
      .finally(() => {if (active) setInternalLoading(false); });
    return () => { active = false; };
  }, [selectedClient?.id, hasSearched, isExternalMode]);

  useEffect(() => {
    setPage(0);
  }, [selectedClient?.id, displayedContracts.length, sortConfig.order, statusFilter]);

  const handleMenuOpen = (event, contract) => {
    setMenuAnchor(event.currentTarget);
    setSelectedContractForMenu(contract);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedContractForMenu(null);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleSortChange = (field, value) => {
    setSortConfig((prev) => ({
      ...prev,
      [field]: value,
    }));
    setPage(0);
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setPage(0);
  };

  // const handleDeleteSuccess = (deletedContractId) => {
  //   setDeleteDialogOpen(false);
  //   handleMenuClose();
  //   if (!isExternalMode) {
  //     setInternalContracts(prev => prev.filter(c => (c.contract_id || c.id) !== deletedContractId));
  //   }
  //   onDeleteSuccess?.(deletedContractId);
  // };

  // const handleDeleteClick = () => {
  //   setDeleteDialogOpen(true);
  //   setMenuAnchor(null);
  // };

  const handleDeleteClick = (contract) => {
    handleMenuClose();

    navigate(`/delete-contract/${contract.contract_id || contract.id}`, {
      state: {
        contract,
        selectedClient,
        source,
      }
    });
  };

  const handleEditClick = (contract) => {
    navigate(`/edit-contract/${contract?.contract_id || contract?.id}`, {
      state: {
        contract,
        selectedClient,
        source
      }
    });
  };

  const handleViewClick = (contract) => {
    navigate(`/view-contract/${contract.contract_id || contract.id}`, {
      state: {
        contract,
        selectedClient,
        source
      }
    });
  };

  //if (displayedContracts.length === 0) {
    //return null;
  //}

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <CardHeader
          title={
            <Typography 
              variant='h6'
              sx={{
                paddingBottom: '0.5rem',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                fontSize: '1rem',
              }}
            >
              {source === "manageClients" ? 'Contracts' : `Contracts for ${selectedClient?.name}`}
            </Typography>
          }
          sx={{ pb: 0, pt: 0, pl: 0 }}
        />
      </Box>
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems='flex-end'>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size='small' disabled={loading}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => handleStatusFilterChange(e.target.value)}
                label='Status'
              >
                <MenuItem value=''>All</MenuItem>
                <MenuItem value='ACTIVE'>Active</MenuItem>
                <MenuItem value='INACTIVE'>Inactive</MenuItem>
                <MenuItem value='EXPIRED'>Expired</MenuItem>
                <MenuItem value='TERMINATED'>Terminated</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size='small' disabled={loading}>
              <InputLabel>Sort Order</InputLabel>
              <Select
                value={sortConfig.order}
                onChange={(e) => handleSortChange('order', e.target.value)}
                label='Sort Order'
              >
                <MenuItem value='desc'>Newest first</MenuItem>
                <MenuItem value='asc'>Oldest first</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate("/create-contract", {
                state: {
                  selectedClient,
                  source
                }
              })}
              disabled={loading}
            >
              Add Contract
            </Button>
          </Grid>
        </Grid>
      </Box>
      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={30} sx={{ mr: 2 }} />
          <Typography>Loading contracts...</Typography>
        </Box>
      ) : sortedContracts.length > 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ backgroundColor: '#f5f5f5'}}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Contract ID</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Start Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>End Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Price</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedContracts.map((contract) => (
                <TableRow key={contract.contract_id || contract.id} hover>
                  <TableCell>{contract.contract_id || contract.id}</TableCell>
                  <TableCell>{contract.contract_start || 'N/A'}</TableCell>
                  <TableCell>{contract.contract_end || 'N/A'}</TableCell>
                  <TableCell>{CURRENCY} {(contract.price_minor / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2}) || 'N/A'}</TableCell>
                  <TableCell>
                    <Typography
                      variant='body2'
                      sx={{
                        display: 'inline-block',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        ...getContractStatusStyles(contract.status),
                      }}
                    >
                      {contract.status || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <Stack direction='row' spacing={1} justifyContent='center'>
                      <Button
                        variant='outlined'
                        size='small'
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleViewClick(contract)}
                      >
                        View
                      </Button>
                      <Button
                        variant='outlined'
                        size='small'
                        startIcon={<EditIcon />}
                        onClick={() => handleEditClick(contract)}
                      >
                        Edit
                      </Button>
                      <IconButton
                        size='small'
                        onClick={(e) => handleMenuOpen(e, contract)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              borderTop: "1px solid #eee",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Results: {totalRows}
            </Typography>

            <TablePagination
              component="div"
              count={totalRows}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[10]}
            />
          </Box>
        </TableContainer>
      )}

      {/* Kebab Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleDeleteClick(selectedContractForMenu)}>
          <DeleteIcon sx={{ mr: 1 }} fontSize='small' />
          Delete
        </MenuItem>
      </Menu>

      {/* Delete Contract Dialog */}
      {/* <DeleteContract
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          handleMenuClose();
        }}
        selectedClient={selectedClient}
        selectedContract={selectedContractForMenu}
        onDeleteSuccess={handleDeleteSuccess}
      /> */}
      {/* No contracts Message */}
      {!loading && (isExternalMode || hasSearched) && sortedContracts.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">
            No contracts found for this client
          </Typography>
        </Box>
      )}
    </>
  );
}


export default ContractsTable;
